import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import CourseTile from '@/components/course/CourseTile.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import Dropdown from '@/components/common/Dropdown.vue'

  import { useRoute } from 'vue-router/composables'
  import useEditor from '@/composables/useEditor'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'

  import eventBus from '@/main'
  import useCourse from '@/composables/useCourse'
  import type { DropdownItem } from '@/services/interfaces/Common'

  interface CourseData {
    courseId: string
    titleHeight: number
    state: string
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'EditorDashboard',
  setup(__props) {

  const route = useRoute()
  const { inEditorMode } = useEditor(route)
  const { courseStateText } = useCourse()
  const { translateString } = useI18n()

  const courseData = ref<CourseData[]>([])
  const searchQuery = ref('')
  const selectedState = ref<string>('all')
  const selectedIddCourse = ref<string>('all')

  const loadingCourses = computed(() => allCourses.value === null)

  const allCourses = computed(() => CourseModule.courses)

  const filteredCourses = computed(() => {
    if (!allCourses.value) return []

    const filtered = allCourses.value.filter((course) => {
      const matchesSearch =
        searchQuery.value === '' || course.title.toLowerCase().includes(searchQuery.value.toLowerCase())

      const matchesState = selectedState.value === 'all' || course.state === selectedState.value

      const matchesIddCourse =
        selectedIddCourse.value === 'all' ||
        (selectedIddCourse.value === 'true' && course.isIddCourse) ||
        (selectedIddCourse.value === 'false' && !course.isIddCourse)

      return matchesSearch && matchesState && matchesIddCourse
    })

    return filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime()
      const dateB = new Date(b.updatedAt).getTime()
      return dateB - dateA
    })
  })

  const availableStates = computed(() => {
    if (!allCourses.value) return []

    const states = [...new Set(allCourses.value.map((course) => course.state))]
    return states.sort()
  })

  const stateDropdownItems = computed((): DropdownItem[] => {
    const items: DropdownItem[] = [
      {
        type: 'all',
        title: translateString('editor.allStates'),
        selectable: true,
      },
    ]

    availableStates.value.forEach((state) => {
      items.push({
        type: state,
        title: courseStateText.value(state),
        selectable: true,
      })
    })

    return items
  })

  const iddCourseDropdownItems = computed((): DropdownItem[] => [
    {
      type: 'all',
      title: translateString('editor.allCourses'),
      selectable: true,
    },
    {
      type: 'true',
      title: translateString('editor.iddCoursesOnly'),
      selectable: true,
    },
    {
      type: 'false',
      title: translateString('editor.nonIddCoursesOnly'),
      selectable: true,
    },
  ])

  const maxHeight = computed(() => {
    if (courseData.value.length === 0) return 0

    const maxTitleHeight = Math.max(...courseData.value.map((course) => course.titleHeight))

    return maxTitleHeight > 0 ? maxTitleHeight : 0
  })

  const onCourseData = (data: CourseData) => {
    const existingIndex = courseData.value.findIndex((course) => course.courseId === data.courseId)

    if (existingIndex >= 0) {
      courseData.value[existingIndex] = data
    } else {
      courseData.value.push(data)
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    selectedState.value = 'all'
    selectedIddCourse.value = 'all'
  }

  const openCreateCourseModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'CreateCourseModal',
      modalProps: {},
      closeModalOnOutsideClick: false,
      cssClass: 'create-course-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  onMounted(() => {
    CourseModule.setCourse(null)
    CourseModule.setCourseBundle(null)
    CourseModule.getCourses()
  })

return { __sfc: true,route, inEditorMode, courseStateText, translateString, courseData, searchQuery, selectedState, selectedIddCourse, loadingCourses, allCourses, filteredCourses, availableStates, stateDropdownItems, iddCourseDropdownItems, maxHeight, onCourseData, clearFilters, openCreateCourseModal, computed, onMounted, ref, LoadingSpinner, CourseTile, KetchUpButton, Dropdown, useRoute, useEditor, useI18n, CourseModule, eventBus, useCourse }
}

})